// src/components/PlannerCategorySelector.js
import React, { useState, useEffect, useRef } from "react"
import { fetchProductStructure } from "../api"
import { getTranslations } from "./translations"

/**
 * Category selector for the planner sidebar
 * Matches ProductSelector styling
 */
const PlannerCategorySelector = ({
  selectedCategory,
  onCategoryChange,
  language = "it",
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [categories, setCategories] = useState([])
  const [categoryData, setCategoryData] = useState({})
  const [loading, setLoading] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)

  const dropdownRef = useRef(null)
  const contentRef = useRef(null)
  const headerClickRef = useRef(false)

  const translations = getTranslations(language)

  // Load categories on mount
  useEffect(() => {
    loadCategories()
  }, [])

  // Height calculation
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight + 20)
    } else {
      setContentHeight(0)
    }
  }, [isOpen, categories])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !headerClickRef.current &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
      headerClickRef.current = false
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const loadCategories = async () => {
    setLoading(true)
    try {
      const structure = await fetchProductStructure()
      const categoryNames = Object.keys(structure)
      setCategories(categoryNames)

      // Build category data for translations
      const catData = {}
      categoryNames.forEach((name) => {
        catData[name] = { name }
      })
      setCategoryData(catData)
    } catch (error) {
      console.error("Failed to load categories:", error)
    } finally {
      setLoading(false)
    }
  }

  const getCategoryName = (categoryKey) => {
    const catData = categoryData[categoryKey]
    if (catData && catData.lang && catData.lang[language]) {
      return catData.lang[language]
    }
    return categoryKey
  }

  const toggleDropdown = (e) => {
    e.stopPropagation()
    headerClickRef.current = true
    setIsOpen((prev) => !prev)
  }

  const handleCategorySelect = (e, category) => {
    e.stopPropagation()
    onCategoryChange(category)
    setIsOpen(false)
  }

  return (
    <div ref={dropdownRef} className="ay-border-b ay-border-gray-300">
      {/* Header */}
      <button
        onClick={toggleDropdown}
        className="ay-w-full ay-flex ay-items-end ay-py-4 ay-px-0 ay-transition-colors"
      >
        <div className="ay-flex ay-w-1/3 md:ay-w-1/5 ay-items-end ay-pb-0.5">
          <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider ay-text-left">
            CATEGORIA
          </span>
        </div>
        <div className="ay-flex ay-w-2/3 md:ay-w-4/5 ay-items-end ay-justify-between">
          <span
            className={`ay-text-base md:ay-text-xl ay-leading-5 ay-font-sans ay-font-normal ${
              selectedCategory ? "ay-text-gray-900" : "ay-text-gray-400"
            }`}
          >
            {selectedCategory
              ? getCategoryName(selectedCategory)
              : translations.allCategories || "Tutte le categorie"}
          </span>
          <svg
            className={`ay-w-4 ay-h-4 ay-text-gray-400 ay-transition-transform ay-duration-300 ${
              isOpen ? "ay-rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown Content */}
      <div
        className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
        style={{ maxHeight: `${contentHeight}px` }}
      >
        <div className="ay-flex">
          <div className="ay-w-1/3 md:ay-w-1/5"></div>
          <div className="ay-w-2/3 md:ay-w-4/5">
            <div
              ref={contentRef}
              className="ay-bg-white ay-max-h-[60vh] ay-overflow-y-auto ay-overflow-x-hidden"
            >
              {loading ? (
                <div className="ay-py-4 ay-text-center ay-text-sm ay-text-gray-500">
                  Caricamento...
                </div>
              ) : (
                <div className="ay-flex ay-flex-col ay-overflow-x-hidden">
                  {/* All Categories Option */}
                  <button
                    onClick={(e) => handleCategorySelect(e, null)}
                    className={`ay-w-full ay-text-left ay-py-0.5 md:ay-py-3 ay-pl-2 ay-font-sans ay-font-bold ay-text-base md:ay-text-[27px] ay-leading-relaxed md:ay-leading-normal ay-transition-[color,transform] ay-duration-200 ay-ease-out ay-origin-left ${
                      !selectedCategory
                        ? "ay-text-gray-500 ay-skew-x-[-4deg]"
                        : "ay-text-gray-700 hover:ay-text-gray-500 hover:ay-skew-x-[-4deg]"
                    }`}
                  >
                    {translations.allCategories || "Tutte le categorie"}
                  </button>

                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={(e) => handleCategorySelect(e, category)}
                      className={`ay-w-full ay-text-left ay-py-0.5 md:ay-py-3 ay-pl-2 ay-font-sans ay-font-bold ay-text-base md:ay-text-[27px] ay-leading-relaxed md:ay-leading-normal ay-transition-[color,transform] ay-duration-200 ay-ease-out ay-origin-left ${
                        selectedCategory === category
                          ? "ay-text-gray-500 ay-skew-x-[-4deg]"
                          : "ay-text-gray-700 hover:ay-text-gray-500 hover:ay-skew-x-[-4deg]"
                      }`}
                    >
                      {getCategoryName(category)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlannerCategorySelector
